'use client';

import { useTranslations } from 'next-intl';
import { QRCodeSVG } from 'qrcode.react';
import { useRef } from 'react';
import logoWhite from '@/app/assets/imgs/logo-white.png';
import type { LiveAvatar } from '@/app/types';
import { useLocale } from '@/i18n/routing';
import { useAvatarDisplayImage } from '../_hooks/use-avatar-display-image';
import { getBannerAvatarCardImage } from '../utils';

interface RoleShareCardProps {
  role: LiveAvatar;
  /** 分享二维码 URL */
  qrCodeUrl?: string;
}

export const RoleShareCard = ({ role, qrCodeUrl }: RoleShareCardProps) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const locale = useLocale();
  const t = useTranslations('vidu-stream.avatars.share');
  const roleImageUri = getBannerAvatarCardImage(role);
  const roleImageUrl = useAvatarDisplayImage(roleImageUri);

  // 格式化日期
  const formattedDate = new Date().toLocaleDateString(locale === 'en' ? 'en-US' : 'zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  return (
    <div
      ref={cardRef}
      className="relative h-[486px] w-[281px] overflow-hidden rounded-tl-[21px] rounded-tr-[21px] bg-[#17181f]"
    >
      {/* 角色图片 */}
      <div className="absolute top-0 left-[-10px] h-[378px] w-[301px]">
        <img alt={role.name} className="size-full object-cover" src={roleImageUrl || ''} />
      </div>

      {/* 局部毛玻璃：三层大范围重叠，并用渐入遮罩柔化每层顶部边界。 */}
      <div
        aria-hidden="true"
        className="absolute inset-x-0 top-[196px] h-[186px] bg-[#17181f]/[0.04] backdrop-blur-[4px] [-webkit-mask-image:linear-gradient(to_bottom,transparent_0%,black_42%,black_100%)] [mask-image:linear-gradient(to_bottom,transparent_0%,black_42%,black_100%)]"
      />
      <div
        aria-hidden="true"
        className="absolute inset-x-0 top-[218px] h-[164px] bg-[#17181f]/[0.07] backdrop-blur-[8px] [-webkit-mask-image:linear-gradient(to_bottom,transparent_0%,black_48%,black_100%)] [mask-image:linear-gradient(to_bottom,transparent_0%,black_48%,black_100%)]"
      />
      <div
        aria-hidden="true"
        className="absolute inset-x-0 top-[240px] h-[142px] bg-[#17181f]/[0.12] backdrop-blur-[14px] [-webkit-mask-image:linear-gradient(to_bottom,transparent_0%,black_52%,black_100%)] [mask-image:linear-gradient(to_bottom,transparent_0%,black_52%,black_100%)]"
      />
      <div
        aria-hidden="true"
        className="absolute inset-x-0 top-[198px] h-[184px] bg-[linear-gradient(to_bottom,rgba(23,24,31,0)_0%,rgba(23,24,31,0.04)_22%,rgba(23,24,31,0.2)_50%,rgba(23,24,31,0.66)_78%,#17181f_100%)]"
      />

      {/* 内容区域 */}
      <div className="absolute top-[275px] left-0 flex w-[281px] flex-col items-center gap-1.5 text-center">
        {/* 角色名称 */}
        <p className="whitespace-nowrap font-['Poppins'] font-bold text-[30px] text-white leading-normal tracking-[-0.4px]">
          {role.name}
        </p>
        {/* 角色描述 */}
        <p className="w-[214px] text-[10.5px] text-white/82 leading-[1.58]">
          {t('cardDescription', { name: role.name || '' })}
        </p>
        {/* 日期 */}
        <p className="w-[281px] text-[#73737d] text-[9px] leading-normal">{formattedDate}</p>
      </div>

      {/* 页脚 */}
      <div className="absolute top-[393px] left-[15px] flex w-[251px] items-center gap-2.5 pt-1">
        {/* 品牌信息 */}
        <div className="flex flex-1 flex-col gap-2">
          {/* Vidu Logo */}
          <div className="h-[15.5px] w-[58px]">
            <img src={logoWhite.src} alt="Vidu" className="size-full object-contain" />
          </div>
          {/* 扫描提示 */}
          <p className="text-left text-[#a1a1ab] text-[9.8px] leading-[1.55]">
            {t('cardScanHint')}
            <br />
            {t('cardScanHintWithName', { name: role.name || '' })}
          </p>
        </div>

        {/* 二维码 */}
        <div className="relative size-[56px] shrink-0 overflow-hidden rounded-md bg-white p-1">
          {qrCodeUrl ? (
            <QRCodeSVG
              value={qrCodeUrl}
              width={48}
              height={48}
              level="H"
              bgColor="#ffffff"
              fgColor="#17171c"
              marginSize={0}
            />
          ) : (
            <div className="flex size-full items-center justify-center text-[9px] text-gray-400">
              {t('cardLoading')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
